import React from 'react';

import { Box, HStack, Skeleton, Stack } from '@chakra-ui/react';

import { CancelButton, PrimaryButton } from 'components/DataEntry/Button';

import {
  CustomField,
  CustomWithLabelField,
  DateRangeField,
  DisplayField,
  MultiTextField,
  ObjectField,
  SelectField,
  TextField,
} from './fields';
import {
  OutlinedFormField,
  OutlinedFormProps,
  OutlinedFormStyles,
} from './types';

const renderField = (field: OutlinedFormField, styles?: OutlinedFormStyles) => {
  switch (field.type) {
    case 'display':
      return <DisplayField field={field} styles={styles} />;
    case 'text':
      return <TextField field={field} styles={styles} />;
    case 'multi-text':
      return <MultiTextField field={field} styles={styles} />;
    case 'select':
      return <SelectField field={field} styles={styles} />;
    case 'dateRange':
      return <DateRangeField field={field} styles={styles} />;
    case 'custom':
      return <CustomField field={field} styles={styles} />;
    case 'custom-with-label':
      return <CustomWithLabelField field={field} styles={styles} />;
    case 'object':
      return (
        <ObjectField field={field} styles={styles}>
          {/* Nested properties use the same loading and style behavior as root fields. */}
          {field.properties.map(property =>
            renderFieldWithSkeleton(property, styles),
          )}
        </ObjectField>
      );
  }
};

const renderFieldWithSkeleton = (
  field: OutlinedFormField,
  styles?: OutlinedFormStyles,
) => (
  <Skeleton
    key={field.name}
    isLoaded={!field.isLoading}
    borderRadius="xl"
    {...styles?.skeleton}
    {...field.styles?.skeleton}
  >
    {renderField(field, styles)}
  </Skeleton>
);

export const OutlinedForm = ({
  fields,
  children,
  title,
  isLoading = false,
  cancelButton,
  submitButton,
  onCancel,
  onSubmit,
  styles,
}: OutlinedFormProps) => (
  <Box
    as="form"
    w="full"
    h="fit-content"
    minH={0}
    alignSelf="flex-start"
    pb={0}
    onSubmit={onSubmit}
    {...styles?.form}
  >
    {title && (
      <Box mb={6} {...styles?.title}>
        {title}
      </Box>
    )}
    <Stack spacing={6} {...styles?.fields}>
      {fields.map(field => (
        <Skeleton
          key={field.name}
          isLoaded={!(isLoading || field.isLoading)}
          borderRadius="xl"
          {...styles?.skeleton}
          {...field.styles?.skeleton}
        >
          {renderField(field, styles)}
        </Skeleton>
      ))}
    </Stack>
    {children}

    {(onCancel || !!submitButton) && (
      <HStack
        position="sticky"
        bottom={0}
        zIndex={1}
        justify="space-between"
        mt={10}
        mb={0}
        bg="white"
        {...styles?.actions}
      >
        {onCancel && (
          <CancelButton
            type="button"
            {...cancelButton}
            // Only rendered when onCancel is set, so the old
            // `?? cancelButton.onClick` fallback could never run.
            onClick={onCancel}
          >
            {cancelButton?.label ?? 'CANCEL'}
          </CancelButton>
        )}

        {!!submitButton && (
          <PrimaryButton type="submit" {...submitButton}>
            {submitButton?.label ?? 'SUBMIT'}
          </PrimaryButton>
        )}
      </HStack>
    )}
  </Box>
);

export * from './types';
export * from './fields';
